/**
 * Central query-key factory (frontend-snappiness plan, Phase 1).
 *
 * Conventions:
 * - Every year-scoped resource keys on the VIEWING year (programYear).
 * - Keys that vary by evaluation context include evaluationType/npi.
 * - Invalidation: prefer targeted `invalidateQueries({ queryKey: qk.x(...) })`
 *   at real data-change points (upload Phase 3 complete, manual edit, measure
 *   selection change, saves). Year switch = invalidate all; logout = clear all
 *   (see src/lib/query-client.ts + AuthContext).
 */
import { queryClient } from './query-client';

/**
 * placeholderData guard: keep the previous query's data ONLY when the entity
 * (practice/ACO) segment of the key is unchanged. Year/NPI/eval switches for
 * the SAME entity hold last-good data (no skeleton flash — locked decision
 * #14); switching to a DIFFERENT entity must NOT flash the old entity's
 * numbers (shows the normal cold-load state instead).
 */
export function keepPreviousSameEntity<TData>(entityIndex: number, entity: string) {
  return (
    previousData: TData | undefined,
    previousQuery: { queryKey?: readonly unknown[] } | undefined,
  ): TData | undefined =>
    previousQuery?.queryKey?.[entityIndex] === entity ? previousData : undefined;
}

export const qk = {
  // Session-scoped (cleared on logout via queryClient.clear)
  userPractices: () => ['user', 'practices'] as const,

  // MIPS practice-scoped
  weights: (practiceId: number | string, year: number) =>
    ['mips', 'weights', String(practiceId), year] as const,
  scores: (practiceId: number | string, year: number) =>
    ['mips', 'scores', String(practiceId), year] as const,
  summaries: (
    practiceId: number | string,
    year: number,
    evaluationType: string,
    npi?: string | null,
  ) => ['performance', 'summaries', String(practiceId), year, evaluationType, npi ?? null] as const,

  // Category calculators (header/composite inputs)
  iaScore: (practiceId: number | string, year: number, npi?: string | null) =>
    ['ia', 'score', String(practiceId), year, npi ?? null] as const,
  costDashboard: (practiceId: number | string, year: number, npi?: string | null) =>
    ['cost', 'dashboard', String(practiceId), year, npi ?? null] as const,

  // Payment adjustment inputs
  paymentAdjConfig: (year: number) => ['payment-adjustment-config', year] as const,
  eligibility: (practiceId: number | string, year: number) =>
    ['eligibility', String(practiceId), year] as const,

  /** Practice profile / special-status flags — Manage MVPs subgroup gate reads this. */
  practiceSettings: (practiceId: number | string, year: number) =>
    ['mvp', 'practice-settings', String(practiceId), year] as const,

  // Patients tab (patient registry — GROUP lane worklist)
  patientsList: (practiceId: number | string, year: number, filters: string) =>
    ['patients', 'list', String(practiceId), year, filters] as const,
  patientDetail: (practiceId: number | string, year: number, patientId: string) =>
    ['patients', 'detail', String(practiceId), year, patientId] as const,
  practiceMeasureSelections: (practiceId: number | string, year: number) =>
    ['practice', 'measure-selections', String(practiceId), year] as const,

  // APP Plus (ACO-scoped)
  acoSubmissions: (acoId: number | string, year: number) =>
    ['aco', String(acoId), 'submissions', year] as const,
  acoConfig: (acoId: number | string, year: number) =>
    ['aco', String(acoId), 'config', year] as const,
  acoGoals: (acoId: number | string, year: number) =>
    ['aco', String(acoId), 'goals', year] as const,
  acoRosterDerived: (acoId: number | string, year: number) =>
    ['aco', String(acoId), 'roster-derived', year] as const,
  acoTinNpiPerformance: (acoId: number | string, year: number, submissionMethod: string | null) =>
    ['aco', String(acoId), 'tin-npi-performance', year, submissionMethod ?? ''] as const,
  acoRoster: (acoId: number | string, year: number) =>
    ['aco', String(acoId), 'roster', year] as const,
  acoParticipants: (acoId: number | string, year: number) =>
    ['aco', String(acoId), 'participants', year] as const,
};

/**
 * "Ring the doorbell" after ANY mutation that changes a practice's scoring
 * inputs (data-freshness review, 2026-08-12): score-submission / cost-feedback
 * / eligibility uploads and deletes, IA selection/attestation saves, PI saves,
 * weights or modeled-percent changes, and score persistence. Invalidates every
 * shared scoring cache for the practice so consumers (Insights, Overview, MVP
 * reports) refetch on their next observation instead of waiting out staleTime.
 *
 * Practice-prefix only — no year segment — on purpose: cost carry-forward
 * means a cost upload into year Y affects every performance year >= Y. The two
 * `mvp` families are invalidated app-wide because their ad-hoc keys store the
 * raw practice id (number or string), so a stringified prefix cannot reliably
 * match them; both are cheap, rarely-observed queries.
 */
export function invalidatePracticeScoring(
  practiceId: number | string | undefined | null,
): void {
  if (practiceId === undefined || practiceId === null || practiceId === '') return;
  const pid = String(practiceId);
  const prefixes: unknown[][] = [
    ['mips', 'scores', pid],
    ['mips', 'weights', pid],
    ['ia', 'score', pid],
    ['cost', 'dashboard', pid],
    ['eligibility', pid],
    ['mvp', 'ia-selections'],
    ['mvp', 'score-submission'],
  ];
  for (const queryKey of prefixes) {
    void queryClient.invalidateQueries({ queryKey });
  }
}
